// 城市数据本地读取工具
// 从 CSV 文件读取城市数据

export interface CityData {
  cityNameEn: string;
  cityNameZh: string;
  countryEn: string;
  countryZh: string;
  countryCode: string;
  flag: string;
  timezone: string;
  population: string;
  alias: string;
  descriptionEn: string;
  descriptionZh: string;
  imagePath: string;
}

// 缓存已解析的城市数据
let cachedCityData: CityData[] | null = null;

/**
 * 解析 CSV 数据
 */
function parseCSV(csvText: string): CityData[] {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',');

  const data: CityData[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    // 处理 CSV 中的引号和逗号
    const values: string[] = [];
    let currentValue = '';
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue);
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    // 添加最后一个值
    values.push(currentValue);

    if (values.length >= 12) {
      data.push({
        cityNameEn: values[0].trim(),
        cityNameZh: values[1].trim(),
        countryEn: values[2].trim(),
        countryZh: values[3].trim(),
        countryCode: values[4].trim(),
        flag: values[5].trim(),
        timezone: values[6].trim(),
        population: values[7].trim(),
        alias: values[8].trim(),
        descriptionEn: values[9].trim(),
        descriptionZh: values[10].trim(),
        imagePath: values[11].trim(),
      });
    }
  }

  return data;
}

/**
 * 加载城市数据
 */
export async function loadCityData(): Promise<CityData[]> {
  // 如果已缓存，直接返回
  if (cachedCityData) {
    return cachedCityData;
  }

  try {
    const response = await fetch('/data/cities.csv');
    if (!response.ok) {
      throw new Error(`Failed to load city data: ${response.status}`);
    }

    const csvText = await response.text();
    cachedCityData = parseCSV(csvText);
    return cachedCityData;
  } catch (error) {
    console.error('Error loading city data:', error);
    return [];
  }
}

/**
 * 根据城市名称获取城市数据（支持中英文）
 */
export async function getCityData(cityName: string): Promise<CityData | null> {
  const allCities = await loadCityData();

  // 精确匹配
  let city = allCities.find(
    c => c.cityNameEn === cityName || c.cityNameZh === cityName
  );

  // 如果没有精确匹配，尝试不区分大小写匹配
  if (!city) {
    const lowerCityName = cityName.toLowerCase();
    city = allCities.find(
      c => c.cityNameEn.toLowerCase() === lowerCityName ||
           c.cityNameZh.toLowerCase() === lowerCityName
    );
  }

  return city || null;
}

/**
 * 获取所有城市列表
 */
export async function getAllCities(): Promise<CityData[]> {
  return await loadCityData();
}

/**
 * 按国家分组获取城市
 */
export async function getCitiesByCountry(): Promise<Record<string, CityData[]>> {
  const allCities = await loadCityData();
  const grouped: Record<string, CityData[]> = {};

  for (const city of allCities) {
    if (!grouped[city.countryCode]) {
      grouped[city.countryCode] = [];
    }
    grouped[city.countryCode].push(city);
  }

  return grouped;
}

/**
 * 搜索城市（支持城市名、国家名、别称搜索）
 */
export async function searchCities(query: string, language: 'en' | 'zh' = 'en'): Promise<CityData[]> {
  const allCities = await loadCityData();
  const lowerQuery = query.toLowerCase();

  return allCities.filter(city => {
    const cityNameEn = city.cityNameEn.toLowerCase();
    const cityNameZh = city.cityNameZh.toLowerCase();
    const countryEn = city.countryEn.toLowerCase();
    const countryZh = city.countryZh.toLowerCase();
    const alias = city.alias.toLowerCase();

    return (
      cityNameEn.includes(lowerQuery) ||
      cityNameZh.includes(lowerQuery) ||
      countryEn.includes(lowerQuery) ||
      countryZh.includes(lowerQuery) ||
      alias.includes(lowerQuery)
    );
  });
}

/**
 * 获取特定国家的所有城市
 */
export async function getCitiesByCountryCode(countryCode: string): Promise<CityData[]> {
  const allCities = await loadCityData();
  return allCities.filter(city => city.countryCode === countryCode);
}
